import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { EventCardActions } from "@/components/event-card-actions";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

import { Calendar, MapPin, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/events/")({
  head: () => ({ meta: [{ title: "Etkinlikler — JUËN" }] }),
  component: EventsListPage,
});

interface EventRow {
  id: string;
  name: string;
  description: string | null;
  starts_at: string;
  venue: string | null;
  cover_image_url: string | null;
}

function EventsListPage() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const tc = useAutoTranslate(events.flatMap((e) => [e.name, e.description, e.venue]));

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user || profile?.membership_status !== "approved") return;
    void (async () => {
      const now = new Date().toISOString();
      const { data } = await supabase
        .from("events")
        .select("id, name, description, starts_at, venue, cover_image_url")
        .eq("status", "published")
        .or(`ends_at.gte.${now},and(ends_at.is.null,starts_at.gte.${now})`)
        .order("starts_at", { ascending: true });
      setEvents((data as EventRow[]) ?? []);
      setLoadingList(false);
    })();
  }, [user, profile]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (profile?.membership_status !== "approved") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.membersOnlyTitle")}</h1>
          <p className="mt-2 text-muted-foreground">
            {profile?.membership_status === "pending"
              ? t("events.membershipPending")
              : t("events.membershipRequired")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("events.upcomingTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("events.upcomingSubtitle")}</p>
        </div>

        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            {t("events.noEvents")}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
              >
                <EventCardActions event={ev} className="absolute bottom-3 right-3 z-10" />
                <Link to="/events/$eventId" params={{ eventId: ev.id }} className="block">
                  <div className="aspect-[16/9] w-full overflow-hidden bg-muted">
                    {ev.cover_image_url ? (
                      <img src={ev.cover_image_url} alt={ev.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">JUËN</div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-semibold">{tc(ev.name)}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(ev.starts_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                      {ev.venue && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" />
                          {tc(ev.venue)}
                        </span>
                      )}
                    </div>
                    {ev.description && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{tc(ev.description)}</p>}
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                      {t("events.detailAndTicket")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
